<template>
  <div>
    <Menu />

    <div v-if="loading" class="event-loading text-center">
      <div class="spinner"></div>
      <h2>Loading event</h2>
    </div>

    <section v-else-if="event" class="event-detail">
      <div class="event-hero">
        <img :src="heroImage" :alt="event.title" class="event-hero__image">
        <div class="event-hero__shade"></div>

        <span class="event-hero__date-tab">{{ formattedDate }}</span>

        <div class="container event-hero__content">
          <div class="row align-items-end">
            <div class="col-lg-8">
              <p v-if="formattedDate" class="event-hero__eyebrow">{{ formattedDate }}</p>
              <h1>{{ event.title }}</h1>
              <p v-if="event.location_name" class="event-hero__location">
                {{ event.location_name }}
              </p>
            </div>
            <div class="col-lg-4 text-lg-end mt-3 mt-lg-0">
              <a :href="ctaHref" class="btn btn-primary event-hero__cta">
                {{ event.cta_label || 'RSVP OR CONTACT' }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <section class="event-about">
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-lg-9 text-center">
              <h2>ABOUT {{ event.title }}</h2>
              <p class="event-copy">{{ event.description || event.summary }}</p>
            </div>
          </div>

          <div v-if="bodyImage" class="row justify-content-center event-feature-image-wrap">
            <div class="col-lg-10">
              <img :src="bodyImage" :alt="event.title" class="event-feature-image">
            </div>
          </div>

          <div class="row justify-content-center event-info-row">
            <div class="col-lg-5 text-center text-lg-start mb-4 mb-lg-0">
              <h3>EVENT HIGHLIGHTS</h3>
              <p class="event-copy">{{ event.highlights || event.summary }}</p>
            </div>

            <div class="col-lg-5 text-center text-lg-start">
              <h3>EVENT LOCATION</h3>
              <p v-if="event.location_name" class="event-location-name">
                <strong>{{ event.location_name }}</strong>
              </p>
              <p v-if="event.location_address" class="event-copy mb-2">
                {{ event.location_address }}
              </p>
              <p v-if="event.location_description" class="event-copy">
                {{ event.location_description }}
              </p>
              <a
                v-if="event.location_url"
                :href="event.location_url"
                class="event-location-link"
                target="_blank"
                rel="noopener"
              >
                VIEW LOCATION
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="event-enquiry" class="event-enquiry-section">
        <div class="container">
          <div class="row justify-content-center">
            <div class="col-lg-10">
              <div class="event-enquiry-box">
                <h3>GET IN TOUCH!</h3>

                <form @submit.prevent="submitForm">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="form.name"
                        placeholder="NAME"
                      >
                      <span class="event-form-error">{{ form.errors.name }}</span>
                    </div>

                    <div class="col-md-6">
                      <input
                        type="email"
                        class="form-control"
                        v-model.trim="form.email"
                        placeholder="EMAIL"
                      >
                      <span class="event-form-error">{{ form.errors.email }}</span>
                    </div>

                    <div class="col-md-6">
                      <input
                        type="tel"
                        class="form-control"
                        v-model.trim="form.phone"
                        placeholder="PHONE"
                      >
                      <span class="event-form-error">{{ form.errors.phone }}</span>
                    </div>

                    <div class="col-md-6">
                      <select class="form-control" v-model="form.enquiry">
                        <option disabled value="">ENQUIRY TYPE</option>
                        <option value="Event RSVP">EVENT RSVP</option>
                        <option value="Event enquiry">EVENT ENQUIRY</option>
                        <option value="General enquiry">GENERAL ENQUIRY</option>
                      </select>
                      <span class="event-form-error">{{ form.errors.enquiry }}</span>
                    </div>

                    <div class="col-12">
                      <textarea
                        class="form-control"
                        v-model.trim="form.message"
                        placeholder="WHAT ARE YOU LOOKING FOR?"
                      ></textarea>
                      <span class="event-form-error">{{ form.errors.message }}</span>
                    </div>
                  </div>

                  <div class="event-enquiry-controls">
                    <div>
                      <div class="form-check mb-2">
                        <input
                          id="eventAcceptTerms"
                          type="checkbox"
                          class="form-check-input"
                          v-model="form.acceptTerms"
                        >
                        <label class="form-check-label" for="eventAcceptTerms">
                          I have read and accept the
                          <a
                            target="_blank"
                            href="https://www.chaseveritt.co.za/disclaimer-policy-website/"
                            class="switch-blue"
                          >terms and conditions</a>
                        </label>
                      </div>

                      <div class="form-check">
                        <input
                          id="eventReceiveMarketing"
                          type="checkbox"
                          class="form-check-input"
                          v-model="form.receiveMarketing"
                        >
                        <label class="form-check-label" for="eventReceiveMarketing">
                          I want to receive marketing material.
                        </label>
                      </div>
                    </div>

                    <button type="submit" class="btn btn-primary" :disabled="!form.acceptTerms || submitting">
                      {{ submitting ? 'SENDING...' : 'SEND MESSAGE' }}
                    </button>
                  </div>

                  <div v-if="successMessage" class="event-form-success">{{ successMessage }}</div>
                  <div v-if="errorMessage" class="event-form-error event-form-error--submit">{{ errorMessage }}</div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>

    <section v-else class="container text-center event-not-found">
      <h1>Event not found</h1>
      <p>The event you are looking for is not currently available.</p>
      <router-link to="/events" class="btn btn-primary">VIEW EVENTS</router-link>
    </section>

    <Footer />
  </div>
</template>

<script>
import axios from 'axios'
import Menu from '@/components/home/Menu.vue'
import Footer from '@/components/home/Footer.vue'

const API_BASE = 'https://luxury-dev.chaseveritt.co.za/api/v3'
const IMAGE_BASE = API_BASE + '/'

export default {
  name: 'Event',

  components: {
    Menu,
    Footer
  },

  data() {
    return {
      event: null,
      loading: true,
      submitting: false,
      successMessage: '',
      errorMessage: '',
      form: {
        name: '',
        email: '',
        phone: '',
        enquiry: '',
        message: '',
        acceptTerms: false,
        receiveMarketing: false,
        errors: {}
      }
    }
  },

  computed: {
    formattedDate() {
      return this.formatEventDate(this.event ? this.event.event_date : '')
    },

    heroImage() {
      if (!this.event) return ''
      return this.resolveImageUrl(this.event.cover_image || this.firstGalleryImage)
    },

    firstGalleryImage() {
      if (!this.event || !Array.isArray(this.event.image_gallery) || !this.event.image_gallery.length) {
        return ''
      }
      return this.event.image_gallery[0].url || ''
    },

    bodyImage() {
      if (!this.event || !Array.isArray(this.event.image_gallery) || !this.event.image_gallery.length) {
        return this.heroImage
      }

      const cover = this.event.cover_image || ''
      const alternate = this.event.image_gallery.find(image => image && image.url && image.url !== cover)
      return this.resolveImageUrl(alternate ? alternate.url : (this.event.image_gallery[0].url || cover))
    },

    ctaHref() {
      if (!this.event || !this.event.cta_url) return '#event-enquiry'
      return this.event.cta_url
    }
  },

  watch: {
    '$route.params.eventId': function () {
      this.fetchEvent()
    }
  },

  mounted() {
    this.fetchEvent()
  },

  methods: {
    async fetchEvent() {
      this.loading = true
      this.event = null

      try {
        const response = await axios.get(API_BASE + '/events.php', {
          params: {
            action: 'list',
            site: 'luxury',
            status: 'published'
          },
          headers: {
            Accept: 'application/json'
          }
        })

        const items = response.data && response.data.success && Array.isArray(response.data.events)
          ? response.data.events
          : []

        const requested = String(this.$route.params.eventId || '').toLowerCase()
        const match = items.find(item => {
          const row = this.getEventRow(item)
          const slug = String(row.slug || '').toLowerCase()
          const ref = String(row.ref_id || (item.meta && item.meta.ref_id) || '').toLowerCase()
          return slug === requested || ref === requested
        })

        if (match) {
          this.event = this.getEventRow(match)
        }
      } catch (error) {
        console.log('Could not load event:', error)
        this.event = null
      }

      this.loading = false
    },

    getEventRow(item) {
      if (
        item &&
        item.event &&
        item.event.data &&
        item.event.data.event &&
        item.event.data.event[0]
      ) {
        return item.event.data.event[0]
      }

      return {}
    },

    resolveImageUrl(value) {
      if (!value) return '/images/luxury-portfolio.png'
      if (value.indexOf('http://') === 0 || value.indexOf('https://') === 0) return value
      return IMAGE_BASE + value.replace(/^\/+/, '')
    },

    formatEventDate(value) {
      if (!value) return ''

      const parts = String(value).split('-')
      if (parts.length !== 3) return value

      const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))

      return new Intl.DateTimeFormat('en-ZA', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      }).format(date)
    },

    validateForm() {
      this.form.errors = {}
      this.successMessage = ''
      this.errorMessage = ''

      if (!this.form.name) this.form.errors.name = 'Name is required'
      if (!this.form.email) this.form.errors.email = 'Email is required'

      if (!this.form.phone) {
        this.form.errors.phone = 'Phone is required'
      } else if (!/^\+?[0-9 ]+$/.test(this.form.phone)) {
        this.form.errors.phone = 'Please enter a valid phone number'
      }

      if (!this.form.enquiry) this.form.errors.enquiry = 'Enquiry type is required'
      if (!this.form.message) this.form.errors.message = 'Message is required'

      return Object.keys(this.form.errors).length === 0
    },

    async submitForm() {
      if (!this.validateForm() || !this.form.acceptTerms || !this.event) return

      this.submitting = true

      const eventReference = this.event.ref_id || this.event.slug || this.event.title
      const message = '[Event: ' + this.event.title + ' / ' + eventReference + ']\n\n' + this.form.message

      try {
        await axios.post('https://stratitude.co.za/api/buyForm.php', {
          name: this.form.name,
          email: this.form.email,
          phone: this.form.phone,
          message: message,
          enquiry: this.form.enquiry,
          receiveMarketing: this.form.receiveMarketing,
          utm_source: localStorage.getItem('utm_source') || '',
          utm_medium: localStorage.getItem('utm_medium') || '',
          utm_campaign: localStorage.getItem('utm_campaign') || '',
          utm_content: localStorage.getItem('utm_content') || '',
          landing_page_url: localStorage.getItem('landing_page_url') || '',
          conversion_page_url: window.location.href
        })

        this.successMessage = 'Email sent successfully.'
        this.form.name = ''
        this.form.email = ''
        this.form.phone = ''
        this.form.enquiry = ''
        this.form.message = ''
        this.form.acceptTerms = false
        this.form.receiveMarketing = false
      } catch (error) {
        console.log('Event enquiry failed:', error)
        this.errorMessage = 'Email sending failed.'
      }

      this.submitting = false
    }
  }
}
</script>

<style scoped>
.event-loading,
.event-not-found {
  padding: 100px 15px;
}

.event-detail {
  background: #fff;
}

.event-hero {
  position: relative;
  min-height: 515px;
  overflow: hidden;
}

.event-hero__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.event-hero__shade {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, .58) 0%, rgba(0, 0, 0, .12) 58%, rgba(0, 0, 0, .03) 100%);
}

.event-hero__date-tab {
  position: absolute;
  top: 0;
  left: 4%;
  z-index: 2;
  min-width: 240px;
  padding: 12px 24px;
  background: #fff;
  color: #255699;
  font-weight: 700;
  text-align: center;
  border-radius: 0 0 5px 5px;
}

.event-hero__content {
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  bottom: 35px;
  color: #fff;
}

.event-hero__eyebrow {
  margin-bottom: 5px;
  font-size: 13px;
}

.event-hero h1 {
  margin-bottom: 4px;
  color: #fff;
  font-size: clamp(36px, 5vw, 65px);
  line-height: 1;
  text-transform: uppercase;
}

.event-hero__location {
  margin: 0;
  font-size: 16px;
}

.event-hero__cta {
  min-width: 230px;
  padding: 11px 28px;
  border-radius: 14px;
  text-transform: uppercase;
}

.event-about {
  padding: 58px 0 24px;
}

.event-about h2,
.event-about h3,
.event-enquiry-box h3 {
  color: #255699;
  text-transform: uppercase;
}

.event-about h2 {
  margin-bottom: 18px;
}

.event-about h3 {
  font-size: 17px;
  margin-bottom: 12px;
}

.event-copy {
  white-space: pre-line;
}

.event-feature-image-wrap {
  margin-top: 42px;
}

.event-feature-image {
  display: block;
  width: 100%;
  height: 390px;
  object-fit: cover;
  border-radius: 5px 5px 26px 5px;
}

.event-info-row {
  margin-top: 32px;
}

.event-location-name {
  margin-bottom: 8px;
}

.event-location-link {
  display: inline-block;
  margin-top: 4px;
  color: #255699;
  font-weight: 700;
  text-decoration: none;
}

.event-enquiry-section {
  padding: 34px 0 70px;
}

.event-enquiry-box {
  padding: 24px 30px 18px;
  background: #f1f1f1;
  border-radius: 4px 4px 22px 4px;
}

.event-enquiry-box h3 {
  margin-bottom: 13px;
  font-size: 16px;
}

.event-enquiry-box .form-control {
  min-height: 38px;
  border: 0;
  background: #fff;
  font-size: 12px;
}

.event-enquiry-box textarea.form-control {
  min-height: 44px;
}

.event-enquiry-controls {
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 24px;
  margin-top: 10px;
}

.event-enquiry-controls .btn {
  min-width: 190px;
  border-radius: 14px;
}

.event-enquiry-box .form-check-label {
  font-size: 11px;
}

.event-form-error {
  display: block;
  min-height: 13px;
  color: #e00134;
  font-size: 11px;
}

.event-form-error--submit {
  margin-top: 10px;
}

.event-form-success {
  margin-top: 10px;
  color: #28a745;
  font-size: 13px;
}

.spinner {
  border: 8px solid rgba(0, 0, 0, 0.1);
  border-left: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1.2s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@media (max-width: 991px) {
  .event-hero {
    min-height: 460px;
  }

  .event-hero__date-tab {
    left: 20px;
  }

  .event-hero__content {
    bottom: 24px;
    padding: 0 12px;
  }

  .event-feature-image {
    height: 300px;
  }
}

@media (max-width: 767px) {
  .event-hero {
    min-height: 430px;
  }

  .event-hero__date-tab {
    left: 12px;
    min-width: 0;
    padding: 9px 14px;
    font-size: 12px;
  }

  .event-hero__cta {
    min-width: 190px;
  }

  .event-about {
    padding-top: 40px;
  }

  .event-feature-image {
    height: 240px;
  }

  .event-info-row {
    margin-top: 25px;
  }

  .event-enquiry-box {
    padding: 20px 18px;
  }

  .event-enquiry-controls {
    display: block;
  }

  .event-enquiry-controls .btn {
    margin-top: 18px;
    width: 100%;
  }
}
</style>
